import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Row, Col, Switch, Checkbox, Popover } from 'antd'
import { CloseOutlined, CheckOutlined } from '@ant-design/icons'
import {
  saveFilter,
  setEnableAllVariant,
  setLockedHandling,
  setSituationFilter,
} from '../../../Slices/Kiosk/QuantitiesSlice'
import QuantitiesTable from '../../../Components/Table/Quantities'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import MenuIcon from '../../../Components/Svg/MenuIcon'
import { renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import { ExistingStock, CheckboxLangClass, ConfigurationForm, Stores } from '../../../Constants/Configuration'
import { getActiveFilterValue } from '../../../Helpers/FilterHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { WorkSpace } from 'Constants/WorkSpace'
import { dispatchablePrepareStores } from 'Helpers/StoreHelper'

function Quantities() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const quantitiesFilters = useAppSelector(state => state.present.quantities.value.filters)
  const enableAllVariant = useAppSelector(state => state.present.quantities.value.enableAllVariant)
  const unlockHandling = useAppSelector(state => state.present.quantities.value.unlockHandling)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const situationFilter = useAppSelector(state => state.present.quantities.value.situationFilter)
  const activeStore = useAppSelector(state => state.present.configuration.activeStore)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const unlockMovementsVisibile = useAppSelector(state => state.present.configuration.isUnlockMovementsVisible)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const quantitiesTabVisible = useAppSelector(state => state.present.configuration.isTabQuantitiesVisibile)
  const workspaceQuantitiesTabVisible = useAppSelector(state => state.present.configuration.workspaceQuantitiesVisible)
  // const isTabQuantitiesFilterNotActiveEnable = useAppSelector((state) => state.present.configuration.workspaceQuantitiesFiltersNotActiveVisible);
  const workspaceUnlockMovementButtonVisible = useAppSelector(
    state => state.present.configuration.workspaceUnlockMovementButtonVisible,
  )
  const hiddenQuantitiesFilter = useAppSelector(state => state.present.configuration.workspaceQuantitiesFilterToHide)
  const quantities = useAppSelector(state => state.present.articleDetail.articleSelected.quantities)
  const [quantitiesCheckboxes, setHandledCheckboxes] = useState(JSON.parse(JSON.stringify(quantitiesFilters)))
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [indeterminate, setIndeterminate] = useState(true)
  const [checkAll, setCheckAll] = useState(false)

  const enableMovementButtonByForm = [
    ConfigurationForm.GRUCCIA,
    ConfigurationForm.POS,
    WorkSpace.BACKOFFICE.toUpperCase(),
    WorkSpace.B2B.toUpperCase(),
    WorkSpace.STOREMANAGER.toUpperCase(),
    WorkSpace.STORE.toUpperCase(),
  ]

  const isArticleSelectedAndDataFetched = articleSelected.fetchedQuantities
  const isConfigurationFormGruccia = formConfigurationValue.toUpperCase() === ConfigurationForm.GRUCCIA
  const isMovementButtonEnabledByFormValue = enableMovementButtonByForm.includes(formConfigurationValue.toUpperCase())
  const showUnlockMovementButton =
    isMovementButtonEnabledByFormValue && unlockMovementsVisibile && workspaceUnlockMovementButtonVisible

  const hasCurrentStoreOption = useMemo(() => dispatch(dispatchablePrepareStores()).length > 0, [activeStore])

  //needed for rerender filter correctly
  useEffect(() => {
    setHandledCheckboxes(JSON.parse(JSON.stringify(quantitiesFilters)))
  }, [quantitiesFilters])

  const onChangeCheckbox = useCallback(
    checkedValues => {
      const currentFilters = JSON.parse(JSON.stringify(quantitiesCheckboxes))

      currentFilters.forEach((element, index) => {
        if (checkedValues.indexOf(element.value) !== -1) {
          element.active = true
        } else {
          element.active = false
        }
      })
      setHandledCheckboxes(currentFilters)

      setIndeterminate(!!checkedValues.length && checkedValues.length < currentFilters.length)
      setCheckAll(checkedValues.length === currentFilters.length)
    },
    [quantitiesCheckboxes],
  )

  const onChangeSituation = useCallback(
    e => {
      dispatch(setSituationFilter(e.target.checked))
    },
    [dispatch],
  )

  const saveQuantitiesFilter = useCallback(() => {
    setPopoverVisible(false)
    dispatch(saveFilter(JSON.parse(JSON.stringify(quantitiesCheckboxes))))
  }, [dispatch, quantitiesCheckboxes])

  const closeQuantitiesFilter = useCallback(() => {
    setHandledCheckboxes(JSON.parse(JSON.stringify(quantitiesFilters)))
    setPopoverVisible(false)
  }, [quantitiesFilters])

  const onCheckAllChange = useCallback(
    e => {
      const currentFilters = JSON.parse(JSON.stringify(quantitiesCheckboxes))
      currentFilters.forEach(element => {
        if (element.value !== ExistingStock) {
          const isCheckboxEnable = hiddenQuantitiesFilter && hiddenQuantitiesFilter.length > 0
          element.active = isCheckboxEnable
            ? e.target.checked && hiddenQuantitiesFilter.indexOf(element.value) === -1
            : e.target.checked
        }
      })

      setHandledCheckboxes(currentFilters)
      setIndeterminate(!e.target.checked ? true : false)
      setCheckAll(e.target.checked)
    },
    [quantitiesCheckboxes, hiddenQuantitiesFilter],
  )

  const handleFilterVisibility = useCallback(
    visible => {
      if (!editTableDisable) {
        return
      }
      if (!visible) {
        setHandledCheckboxes(JSON.parse(JSON.stringify(quantitiesFilters)))
      }
      setPopoverVisible(visible)
    },
    [quantitiesFilters, editTableDisable],
  )

  const handlingCheckboxContent = useMemo(() => {
    return (
      <>
        <Checkbox
          className='quantities-checkbox select-all general-checkbox'
          indeterminate={indeterminate}
          onChange={onCheckAllChange}
          checked={checkAll}
        >
          {renderTranslation(language, TranslationKeys.checkboxSelectDeselectAllLabel)}
        </Checkbox>
        <Checkbox.Group
          className='quantities-checkbox-container'
          onChange={checkedValues => {
            onChangeCheckbox(checkedValues)
          }}
          value={getActiveFilterValue(quantitiesCheckboxes)}
        >
          <Row onClick={stopPropagation}>
            {quantitiesFilters.map(({ value, active, visible }) => {
              return visible ? (
                <Col key={value} span={24}>
                  {value === ExistingStock ? (
                    <Checkbox className={'quantities-checkbox general-checkbox disable'} value={value} disabled>
                      {renderTranslation(language, TranslationKeys[CheckboxLangClass + setCapitalLetter(value)])}
                    </Checkbox>
                  ) : (
                    <Checkbox
                      className={
                        'quantities-checkbox general-checkbox' +
                        (hiddenQuantitiesFilter &&
                        hiddenQuantitiesFilter.length > 0 &&
                        hiddenQuantitiesFilter.indexOf(value) !== -1
                          ? ' disable'
                          : '')
                      }
                      value={value}
                      disabled={
                        hiddenQuantitiesFilter && hiddenQuantitiesFilter.length > 0
                          ? hiddenQuantitiesFilter.indexOf(value) !== -1
                          : false
                      }
                    >
                      {renderTranslation(language, TranslationKeys[CheckboxLangClass + setCapitalLetter(value)])}
                    </Checkbox>
                  )}
                </Col>
              ) : null
            })}
          </Row>
        </Checkbox.Group>
      </>
    )
  }, [
    quantitiesFilters,
    indeterminate,
    onCheckAllChange,
    checkAll,
    language,
    onChangeCheckbox,
    quantitiesCheckboxes,
    hiddenQuantitiesFilter,
  ])

  const handlingCheckboxTitle = useMemo(
    () => (
      <Row
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <Col span={24} className='quantities-checkbox-title'>
          <div className='quantities-checkbox-title-head'>
            {renderTranslation(language, TranslationKeys.quantitiesCheckboxTitle)}
            <span className='float-right'>
              <CheckOutlined
                className='quantities-check-icon general-check-icon'
                onClick={e => {
                  e.stopPropagation()
                  saveQuantitiesFilter()
                }}
              />
              <CloseOutlined
                className='quantities-close-icon general-clear-icon'
                onClick={e => {
                  e.stopPropagation()
                  closeQuantitiesFilter()
                }}
              />
            </span>
          </div>
        </Col>
      </Row>
    ),
    [language, saveQuantitiesFilter, closeQuantitiesFilter],
  )

  return quantitiesTabVisible && workspaceQuantitiesTabVisible && quantities?.length > 0 ? (
    <Row>
      <Col className='pt-2 pl-2' span={24}>
        <Row className='w-100'>
          {!isConfigurationFormGruccia ? (
            <Col>
              <Popover
                id='quantitiesCheckboxContent'
                title={handlingCheckboxTitle}
                content={handlingCheckboxContent}
                placement='bottomLeft'
                autoAdjustOverflow={true}
                trigger='click'
                open={popoverVisible}
                onOpenChange={open => {
                  handleFilterVisibility(open)
                }}
              >
                <MenuIcon
                  onClick={stopPropagation}
                  className={'generic-icon menu-icon' + (!editTableDisable ? ' disable' : '')}
                />
              </Popover>
            </Col>
          ) : null}
          {!isConfigurationFormGruccia ? (
            <Col>
              <Switch
                checked={enableAllVariant}
                className='custom-switch article-switch'
                onClick={(checked, event) => {
                  event.stopPropagation()
                }}
                checkedChildren={renderTranslation(language, TranslationKeys.articleSwitchUncheck)}
                unCheckedChildren={renderTranslation(language, TranslationKeys.articleSwitchUncheck)}
                onChange={(checked, event) => {
                  dispatch(setEnableAllVariant(!checked))
                }}
                disabled={!isArticleSelectedAndDataFetched}
              />
            </Col>
          ) : null}
          {activeStore === Stores.Remote && !isConfigurationFormGruccia ? (
            <Col>
              <div className='quantities-condition-box'>
                <Checkbox
                  className='quantities-condition-checkbox general-checkbox w-100'
                  checked={situationFilter}
                  onChange={onChangeSituation}
                  disabled={!isArticleSelectedAndDataFetched || enableAllVariant}
                >
                  {renderTranslation(language, TranslationKeys.quantitiesSituationFilter)}
                </Checkbox>
              </div>
            </Col>
          ) : null}
          {showUnlockMovementButton ? (
            <Col flex='auto' className='align-center'>
              <Switch
                checked={unlockHandling}
                className='custom-switch handling-switch float-right'
                onClick={(checked, event) => {
                  event.stopPropagation()
                }}
                checkedChildren={renderTranslation(language, TranslationKeys.lockedHandlingSwitchUncheck)}
                unCheckedChildren={renderTranslation(language, TranslationKeys.lockedHandlingSwitchCheck)}
                onChange={(checked, event) => {
                  if (!isConfigurationFormGruccia) {
                    dispatch(setLockedHandling(!checked))
                  }
                }}
                disabled={!isArticleSelectedAndDataFetched || enableAllVariant || !hasCurrentStoreOption}
              />
            </Col>
          ) : null}
        </Row>
      </Col>
      <Row className='pa-2'>
        <Col span={24} className='overflow-hidden overflow-x-auto  custom-scroll'>
          <QuantitiesTable />
        </Col>
      </Row>
    </Row>
  ) : null
}

export default Quantities
